/**
 * An issue's files as the details panel's MEDIA section shows them.
 *
 * The API keeps three lists on an issue: `pictures`, `videos` and
 * `attachments` (documents, PDFs and any other file; mobile files them
 * there). app-pictures-section draws each file by its `fileType` and has no
 * default case, so every file needs one: picture, video, or document (a
 * thumbnail when the file has one, the file name otherwise; a click opens it
 * like any other media).
 */

export type IssueMediaKind = 'pictures' | 'videos' | 'attachments';

export interface IssueMediaFile {
  id?: string;
  fileType?: string;
}

export interface IssueMediaLists<F extends IssueMediaFile = IssueMediaFile> {
  pictures?: F[] | null;
  videos?: F[] | null;
  attachments?: F[] | null;
}

const FILE_TYPE: Record<IssueMediaKind, string> = {
  pictures: 'picture',
  videos: 'video',
  attachments: 'document',
};

/** The list an upload of this fileType is saved under. */
export function issueMediaKindOf(fileType: string | null | undefined): IssueMediaKind | null {
  switch (fileType) {
    case 'picture':
      return 'pictures';
    case 'video':
      return 'videos';
    case 'document':
      return 'attachments';
    default:
      return null;
  }
}

/**
 * Tags the files of one list with their fileType, in place (the panel and
 * the lists share the objects), keeping a type already set. Returns the list,
 * [] for a missing one.
 */
export function tagIssueMedia<F extends IssueMediaFile>(files: F[] | null | undefined, kind: IssueMediaKind): F[] {
  const list = Array.isArray(files) ? files : [];
  list.forEach(file => {
    if (file && !file.fileType) {
      file.fileType = FILE_TYPE[kind];
    }
  });
  return list;
}

/** Pictures, then videos, then attachments, all tagged: the MEDIA section's list. */
export function issueMediaFiles<F extends IssueMediaFile>(issue: IssueMediaLists<F> | null | undefined): F[] {
  return [
    ...tagIssueMedia(issue?.pictures, 'pictures'),
    ...tagIssueMedia(issue?.videos, 'videos'),
    ...tagIssueMedia(issue?.attachments, 'attachments'),
  ];
}

/**
 * Ids of a media list held as JSON in a form control (the issue form keeps
 * them that way): what the API takes on save. Anything unreadable is [].
 */
export function issueMediaIds(json: string | null | undefined): string[] {
  try {
    const list = JSON.parse(json || '[]');
    return Array.isArray(list) ? list.map(item => item?.id).filter(id => !!id) : [];
  } catch {
    return [];
  }
}
